import React from "react";
import { useKPAIS } from "@hooks/dashboard/useKapi";

const CardItem: React.FC<{
  label: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  comparison: string;
  percentageChange: number;
}> = ({ label, value, icon, color, bgColor, comparison, percentageChange }) => {
  const isPositive = comparison === "up";
  return (
    <div className="kpi-card">
      <div className="kpi-top">
        <div className="kpi-icon" style={{ background: bgColor }}>
          {icon}
        </div>
        <div className={`kpi-change ${isPositive ? 'up' : 'down'}`}>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
            {isPositive ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
          </svg>
          {percentageChange}%
        </div>
      </div>
      <div className="kpi-bottom">
        <span className="kpi-value" style={{ color }}>{value}</span>
        <span className="kpi-label">{label}</span>
      </div>
    </div>
  );
};

const Card: React.FC = () => {
  const { data, isLoading } = useKPAIS();

  if (isLoading) return (
    <div className="kpi-grid">
      {[1,2,3,4].map(i => <div key={i} className="kpi-card kpi-skeleton"><div className="kpi-skeleton-bar"/><div className="kpi-skeleton-bar short"/></div>)}
    </div>
  );

  const cardsData = [
    {
      label: "Total Food Courts",
      value: data?.totalFoodcourtCount || 0,
      icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
      color: "#3B82F6",
      bgColor: "linear-gradient(135deg, #3B82F6, #2563EB)",
      comparison: "up",
      percentageChange: 0,
    },
    {
      label: "Total Orders",
      value: data?.totalOrders || 0,
      icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>,
      color: "#06B6D4",
      bgColor: "linear-gradient(135deg, #06B6D4, #0891B2)",
      comparison: data?.totalOrdersComparison || "up",
      percentageChange: data?.totalOrdersPercentageChange || 0,
    },
    {
      label: "Total Order Value",
      value: `₹${(data?.totalOrderValue || 0).toFixed(2)}`,
      icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
      color: "#10B981",
      bgColor: "linear-gradient(135deg, #10B981, #059669)",
      comparison: data?.totalOrderValueComparison || "up",
      percentageChange: data?.totalOrderValuePercentageChange || 0,
    },
    {
      label: "Platform Fees",
      value: `₹${(data?.totalPlatformFee || 0).toFixed(2)}`,
      icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>,
      color: "#F59E0B",
      bgColor: "linear-gradient(135deg, #F59E0B, #D97706)",
      comparison: data?.totalPlatformFeeComparison || "up",
      percentageChange: data?.totalPlatformFeePercentageChange || 0,
    },
  ];

  return (
    <div className="kpi-grid">
      {cardsData.map((card, index) => (
        <CardItem key={index} {...card} />
      ))}
    </div>
  );
};

export default Card;
